import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import BusinessLayout from '../../components/business/BusinessLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import ReviewActionDrawer from '../../components/business/ReviewActionDrawer';
import { fetchBusinessReviews } from '../../api/business';

const StarRating = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width="13" height="13" viewBox="0 0 24 24"
          fill={i <= full ? '#3b82f6' : (i === full + 1 && half ? 'url(#half)' : 'none')}
          stroke="#3b82f6" strokeWidth="1.5">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '–';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
};

const BusinessReviewsPage = () => {
  const [searchParams] = useSearchParams();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(() => ({
    status: searchParams.get('status') || 'all',
    rating: searchParams.get('rating') || 'all',
    search: '',
    sort_by: 'created_at',
    order: 'desc',
  }));
  const [pagination, setPagination] = useState({
    limit: 10,
    offset: 0,
    total: 0,
  });

  // Fetch reviews when filters or pagination change
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchBusinessReviews({
          ...filters,
          limit: pagination.limit,
          offset: pagination.offset,
        });
        setReviews(data.reviews || []);
        setPagination((prev) => ({
          ...prev,
          total: data.total || 0,
        }));
      } catch (err) {
        setError(err?.response?.data?.message || 'Failed to load reviews');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters, pagination.limit, pagination.offset]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({
      ...prev,
      offset: 0, // Reset pagination on filter change
    }));
  };

  const handleSearch = (value) => {
    handleFilterChange('search', value);
  };

  const handleOpenReviewDrawer = (review) => {
    setSelectedReview(review);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedReview(null);
  };

  const handleActionComplete = () => {
    // Refresh reviews after action
    const load = async () => {
      try {
        const data = await fetchBusinessReviews({
          ...filters,
          limit: pagination.limit,
          offset: pagination.offset,
        });
        setReviews(data.reviews || []);
      } catch (err) {
        console.error('Failed to refresh reviews:', err);
      }
    };
    load();
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const startIndex = pagination.offset + 1;
  const endIndex = Math.min(pagination.offset + pagination.limit, pagination.total);

  return (
    <BusinessLayout title="Reviews">
      <Breadcrumb 
        items={[
          { label: 'Dashboard', href: '/business/dashboard' },
          { label: 'Reviews', href: '/business/reviews' }
        ]} 
      />

      <div className="mb-6">
        <h2 className="text-[20px] font-bold text-slate-900 mb-1">Reviews Management</h2>
        <p className="text-[13px] text-slate-600">View and manage all reviews received by your business</p>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="grid grid-cols-4 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Reviews</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="flagged">Flagged</option>
            </select>
          </div>

          {/* Rating Filter */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Rating
            </label>
            <select
              value={filters.rating}
              onChange={(e) => handleFilterChange('rating', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Sort By
            </label>
            <select
              value={filters.sort_by}
              onChange={(e) => handleFilterChange('sort_by', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="created_at">Most Recent</option>
              <option value="rating">Rating</option>
              <option value="helpful_count">Most Helpful</option>
            </select>
          </div>

          {/* Order Direction */}
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 uppercase tracking-wider mb-2">
              Order
            </label>
            <select
              value={filters.order}
              onChange={(e) => handleFilterChange('order', e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        {/* Search Box */}
        <div className="mt-4 pt-4 border-t border-slate-200">
          <input
            type="text"
            placeholder="Search reviews by title or content..."
            value={filters.search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-4 py-2 border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Reviews List Section */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h3 className="text-[14px] font-semibold text-slate-900">
            Reviews ({pagination.total})
          </h3>
          {pagination.total > 0 && (
            <p className="text-[12px] text-slate-600">
              Showing {startIndex} to {endIndex} of {pagination.total}
            </p>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="p-6 text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-[13px]">
              {error}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && reviews.length === 0 && (
          <div className="p-12 text-center">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" className="mx-auto mb-4 opacity-40">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            <p className="text-[13px] text-slate-500">No reviews found with current filters</p>
          </div>
        )}

        {/* Reviews Table */}
        {!loading && !error && reviews.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Reviewer</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Rating</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Comment</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Posted</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review, idx) => (
                  <tr 
                    key={review.id || idx}
                    onClick={() => handleOpenReviewDrawer(review)}
                    className="border-b border-slate-100 hover:bg-blue-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <p className="text-[12px] font-medium text-slate-900">{review.reviewer_name || '–'}</p>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="text-[12px] text-slate-700 truncate font-medium">{review.title || '–'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <StarRating rating={review.rating || 0} />
                        <span className="text-[12px] font-medium text-slate-600 ml-1">{review.rating || '–'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      <p className="text-[12px] text-slate-600 line-clamp-2">{(review.content || '–').substring(0, 80)}...</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-[12px] text-slate-600">{timeAgo(review.created_at)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex rounded-full px-2 py-1 text-[11px] font-semibold
                        ${review.is_approved
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                        }`}>
                        {review.is_approved ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenReviewDrawer(review);
                        }}
                        className="text-[12px] font-medium text-blue-600 hover:text-blue-700 transition-colors"
                        title="View review details"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-[12px] text-slate-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPagination((prev) => ({
                ...prev,
                offset: Math.max(0, prev.offset - prev.limit),
              }))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-slate-200 rounded-lg text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPagination((prev) => ({
                ...prev,
                offset: prev.offset + prev.limit,
              }))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-slate-200 rounded-lg text-[12px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Review Action Drawer */}
      <ReviewActionDrawer
        review={selectedReview}
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        onActionComplete={handleActionComplete}
      />
    </BusinessLayout>
  );
};

export default BusinessReviewsPage;
